"use client";

import { FormEvent, useEffect, useState } from "react";
import { Shield } from "lucide-react";

interface ContactFormProps {
  showTitle?: boolean;
}

const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

const loadRecaptcha = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      return reject(new Error("No window object"));
    }

    if ((window as any).grecaptcha) {
      return resolve();
    }

    const existingScript = document.querySelector<HTMLScriptElement>("script[src*='recaptcha']");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () => reject(new Error("reCAPTCHA failed to load")));
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("reCAPTCHA failed to load"));
    document.body.appendChild(script);
  });
};

const getReCaptchaToken = async () => {
  if (!recaptchaSiteKey || typeof window === "undefined") {
    return undefined;
  }

  await loadRecaptcha();
  return new Promise<string>((resolve, reject) => {
    const grecaptcha = (window as any).grecaptcha;
    if (!grecaptcha) {
      return reject(new Error("reCAPTCHA not loaded"));
    }
    grecaptcha.ready(() => {
      grecaptcha.execute(recaptchaSiteKey, { action: "contact_form" }).then(resolve).catch(reject);
    });
  });
};

export default function ContactForm({ showTitle = false }: ContactFormProps) {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success">("idle");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  useEffect(() => {
    if (!recaptchaSiteKey) {
      return;
    }

    loadRecaptcha()
      .then(() => setRecaptchaLoaded(true))
      .catch(() => setRecaptchaLoaded(false));
  }, []);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string) => {
    return /^\+?[\d\s-]{8,}$/.test(phone);
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setServerError(null);

    const formData = new FormData(form);
    const nimi = (formData.get("nimi") as string) || "";
    const telefon = (formData.get("telefon") as string) || "";
    const email = (formData.get("email") as string) || "";
    const ettevote = (formData.get("ettevote") as string) || "";
    const teade = (formData.get("teade") as string) || "";

    const newErrors: { [key: string]: string } = {};

    if (!nimi.trim()) newErrors.nimi = "Nimi on kohustuslik";
    if (!telefon.trim() || !validatePhone(telefon)) newErrors.telefon = "Sisestage korrektne telefoninumber";
    if (!email.trim() || !validateEmail(email)) newErrors.email = "Sisestage korrektne e-posti aadress";
    if (!teade.trim()) newErrors.teade = "Teade on kohustuslik";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setFormStatus("submitting");

    let token: string | undefined;
    if (recaptchaSiteKey) {
      try {
        token = await getReCaptchaToken();
      } catch (error) {
        console.error("reCAPTCHA error", error);
      }
    }

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nimi, telefon, email, ettevote, teade, token }),
      });

      const data = await response.json();
      if (!response.ok || data.error) {
        setServerError(data.error || "Sõnumi saatmine ebaõnnestus. Proovi hiljem uuesti.");
        setFormStatus("idle");
        return;
      }

      setFormStatus("success");
      form.reset();
    } catch (error) {
      console.error("Contact form fetch error", error);
      setServerError("Sõnumi saatmine ebaõnnestus. Proovi hiljem uuesti.");
      setFormStatus("idle");
    }
  };

  if (formStatus === "success") {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center min-h-[400px]">
        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-6">
          <Shield className="w-8 h-8 text-accent" />
        </div>
        <h3 className="font-heading font-bold text-[24px] text-text-main mb-4">
          Täname!
        </h3>
        <p className="font-body text-text-muted text-[16px]">
          Võtame teiega ühendust 24 tunni jooksul.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleFormSubmit} className="flex flex-col gap-6" noValidate>
      {showTitle && (
        <h3 className="font-heading font-bold text-[24px] text-text-main mb-2">
          Saada meile sõnum
        </h3>
      )}

      {serverError && (
        <div className="rounded-sm border border-red-500 bg-red-500/10 p-4 text-red-500 text-sm">
          {serverError}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label htmlFor="nimi" className="font-body text-[14px] text-text-main">
          Nimi *
        </label>
        <input
          id="nimi"
          name="nimi"
          type="text"
          className={`bg-secondary border ${errors.nimi ? "border-red-500 focus:border-red-500" : "border-white/10 focus:border-accent"} outline-none text-text-main placeholder-text-muted px-4 py-3.5 rounded-sm transition-colors font-body text-base min-h-[48px] focus-visible:ring-2 focus-visible:ring-accent/50`}
          placeholder="Teie nimi"
        />
        <div className="min-h-[20px]">
          {errors.nimi && <span className="text-red-500 text-[12px] font-body">{errors.nimi}</span>}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="ettevote" className="font-body text-[14px] text-text-main">
          Ettevõte
        </label>
        <input
          id="ettevote"
          name="ettevote"
          type="text"
          className="bg-secondary border border-white/10 focus:border-accent outline-none text-text-main placeholder-text-muted px-4 py-3.5 rounded-sm transition-colors font-body text-base min-h-[48px] focus-visible:ring-2 focus-visible:ring-accent/50"
          placeholder="Ettevõtte nimi (valikuline)"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <label htmlFor="telefon" className="font-body text-[14px] text-text-main">
            Telefoninumber *
          </label>
          <input
            id="telefon"
            name="telefon"
            type="tel"
            className={`bg-secondary border ${errors.telefon ? "border-red-500 focus:border-red-500" : "border-white/10 focus:border-accent"} outline-none text-text-main placeholder-text-muted px-4 py-3.5 rounded-sm transition-colors font-body text-base min-h-[48px] focus-visible:ring-2 focus-visible:ring-accent/50`}
            placeholder="+372"
          />
          <div className="min-h-[20px]">
            {errors.telefon && <span className="text-red-500 text-[12px] font-body">{errors.telefon}</span>}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="email" className="font-body text-[14px] text-text-main">
            E-posti aadress *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            className={`bg-secondary border ${errors.email ? "border-red-500 focus:border-red-500" : "border-white/10 focus:border-accent"} outline-none text-text-main placeholder-text-muted px-4 py-3.5 rounded-sm transition-colors font-body text-base min-h-[48px] focus-visible:ring-2 focus-visible:ring-accent/50`}
            placeholder="nimi@email.ee"
          />
          <div className="min-h-[20px]">
            {errors.email && <span className="text-red-500 text-[12px] font-body">{errors.email}</span>}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="teade" className="font-body text-[14px] text-text-main">
          Teade *
        </label>
        <textarea
          id="teade"
          name="teade"
          rows={4}
          className={`bg-secondary border ${errors.teade ? "border-red-500 focus:border-red-500" : "border-white/10 focus:border-accent"} outline-none text-text-main placeholder-text-muted px-4 py-3.5 rounded-sm transition-colors font-body text-base min-h-[120px] resize-none focus-visible:ring-2 focus-visible:ring-accent/50`}
          placeholder="Kuidas saame aidata?"
        ></textarea>
        <div className="min-h-[20px]">
          {errors.teade && <span className="text-red-500 text-[12px] font-body">{errors.teade}</span>}
        </div>
      </div>

      <button
        disabled={formStatus === "submitting"}
        type="submit"
        className="bg-accent text-primary font-heading font-bold px-8 py-4 rounded-sm hover:bg-accent/90 transition-colors duration-200 mt-2 min-h-[48px] disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
      >
        {formStatus === "submitting" ? "Saadan..." : "Saada"}
      </button>
    </form>
  );
}
